import LazyWidgetController from "./lazy_widget_controller"

// Stimulus controller wrapping CodeMirror 6 for the .code-mirror-editor textarea.
//
// Lifecycle (lazy import + Turbo-cache-safe teardown) is handled by
// LazyWidgetController. code-editor does NOT re-parent its textarea (it inserts
// the editor as a SIBLING and hides the textarea), so it can't loop; it overrides
// destroy() to undo those DOM side-effects.
//
// Behaviour mirrors the legacy CM5 init: line numbers, active-line highlight,
// history + sublime-ish keymap, bracket matching, indent-on-input, syntax
// highlighting, line wrapping, One Dark theme. An updateListener mirrors the doc
// back into the textarea so plain form submits send the edited content, and the
// view is exposed on the textarea as `_cmView` (the settings-form controller
// reads it to validate JSON / insert a code).
//
// Language mapping (from data-mode, emitted by AppFormBuilder#formatted_code_area):
//   application/javascript            -> @codemirror/lang-javascript  javascript()
//   application/json                  -> @codemirror/lang-json        json()
//   text/html, application/x-erb      -> @codemirror/lang-html        html()
//   text/x-sass                       -> @codemirror/lang-css         css()
//   ruby                              -> @codemirror/legacy-modes ruby via StreamLanguage
//   anything else ("plain")           -> no language extension
//
// Usage in views (rendered by AppFormBuilder app_code_area / app_text_area):
//   <textarea data-controller="code-editor" class="code-mirror-editor"
//             data-mode="application/json" id="setting_content" ...></textarea>
//
export default class extends LazyWidgetController {
  async load() {
    // Resolve the language extension for this textarea's mode (lazy, per-mode)
    // plus the CodeMirror core packages.
    const languageExtension = await this.languageExtensionFor(this.element.dataset.mode)
    const [stateMod, viewMod, commandsMod, languageMod, themeMod] = await Promise.all([
      import("@codemirror/state"),
      import("@codemirror/view"),
      import("@codemirror/commands"),
      import("@codemirror/language"),
      import("@codemirror/theme-one-dark"),
    ])
    return { languageExtension, stateMod, viewMod, commandsMod, languageMod, oneDark: themeMod.oneDark }
  }

  build(loaded) {
    const textarea = this.element
    const { EditorState } = loaded.stateMod
    const { EditorView, lineNumbers, highlightActiveLine, keymap } = loaded.viewMod
    const { history, historyKeymap, defaultKeymap, indentWithTab } = loaded.commandsMod
    const { bracketMatching, indentOnInput, syntaxHighlighting, defaultHighlightStyle } = loaded.languageMod
    const oneDark = loaded.oneDark

    const extensions = [
      lineNumbers(),
      highlightActiveLine(),
      history(),
      keymap.of([...defaultKeymap, ...historyKeymap, indentWithTab]),
      bracketMatching(),
      indentOnInput(),
      syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
      EditorView.lineWrapping,
      oneDark,
    ]

    if (loaded.languageExtension) {
      extensions.push(loaded.languageExtension)
    }

    // Mirror the document back into the textarea on change so form submits work.
    extensions.push(
      EditorView.updateListener.of((v) => {
        if (v.docChanged) {
          textarea.value = v.state.doc.toString()
        }
      })
    )

    const editorView = new EditorView({
      state: EditorState.create({
        doc: textarea.value,
        extensions: extensions,
      }),
    })

    // Insert the editor after the textarea, hide the textarea, expose the view.
    textarea.insertAdjacentElement("afterend", editorView.dom)
    textarea.style.display = "none"
    textarea._cmView = editorView
    return editorView
  }

  destroy(editorView) {
    try { editorView.destroy() } catch (e) { /* already gone */ }
    this.element._cmView = null
    // Unhide the original textarea.
    this.element.style.display = ""
  }

  // Lazily import the language package matching the given mode string and
  // return its CodeMirror extension (or null for "plain"/unknown modes).
  async languageExtensionFor(mode) {
    switch (mode) {
      case "application/javascript": {
        const { javascript } = await import("@codemirror/lang-javascript")
        return javascript()
      }
      case "application/json": {
        const { json } = await import("@codemirror/lang-json")
        return json()
      }
      case "text/html":
      case "application/x-erb": {
        const { html } = await import("@codemirror/lang-html")
        return html()
      }
      case "text/x-sass": {
        const { css } = await import("@codemirror/lang-css")
        return css()
      }
      case "ruby": {
        const [{ StreamLanguage }, { ruby }] = await Promise.all([
          import("@codemirror/language"),
          import("@codemirror/legacy-modes/mode/ruby"),
        ])
        return StreamLanguage.define(ruby)
      }
      default:
        return null
    }
  }
}
